/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ConferenceProfilesPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Conference Profiles management page with data grid, search, filtering,
    and CRUD operations for FreeSWITCH conference profiles.

 ?? Notes:
    - Lists all conference profiles (system and custom)
    - Supports search, filtering, and pagination
    - Provides actions: View, Edit, Duplicate, Delete, View XML
    - Integrates with debug mode for XML viewing
    - Uses consistent UI patterns from other modules

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra
----------------------------------------------------------------*/

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useAuthStore } from '../../store/auth';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { Badge } from '../../components/ui/badge';
import { ModulePageContainer } from '../../components/layouts';
import {
  Edit,
  Trash2,
  Copy,
  Code,
  Settings,
  Shield,
  X
} from 'lucide-react';
import { useConferenceProfileList } from '../conferenceProfiles/hooks';
import { ConferenceProfile } from '../conferenceProfiles/types';
import { deleteConferenceProfile, generateConferenceProfileXml } from '../conferenceProfiles/api';
import { navigateToConferenceProfileDuplicate } from './conferenceProfilesActions';

export default function ConferenceProfilesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();

  // State for confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<ConferenceProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for XML modal
  const [xmlModal, setXmlModal] = useState<{ isOpen: boolean; profile: ConferenceProfile | null; xml: string }>({
    isOpen: false,
    profile: null,
    xml: '',
  });

  // Available columns
  const availableColumns = [
    {
      key: 'name',
      label: t('conferenceProfiles.columns.name'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => {
        if (!item) return '-';
        return (
          <div className="flex items-center gap-2">
            {item.isSystem ? (
              <Shield className="h-4 w-4 text-blue-500" />
            ) : (
              <Settings className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium">{item.name || '-'}</span>
          </div>
        );
      }
    },
    {
      key: 'description',
      label: t('conferenceProfiles.columns.description'),
      sortable: false,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => {
        if (!item) return '-';
        return (
          <span className="text-muted-foreground">
            {item.description || '-'}
          </span>
        );
      }
    },
    {
      key: 'type',
      label: t('conferenceProfiles.columns.type'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => {
        if (!item) return '-';
        return (
          <Badge
            variant={item.isSystem ? 'default' : 'secondary'}
            size="sm"
          >
            {item.isSystem ? t('conferenceProfiles.types.system') : t('conferenceProfiles.types.custom')}
          </Badge>
        );
      }
    },
    {
      key: 'enabled',
      label: t('conferenceProfiles.columns.status'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => {
        if (!item) return '-';
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: t('conferenceProfiles.columns.createdAt'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => {
        if (!item || !item.createdAt) return '-';
        return (
          <span className="text-sm text-muted-foreground">
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        );
      }
    }
  ];

  // Add Actions column
  const columnsWithActions = [
    ...availableColumns,
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      visible: true,
      render: (_: unknown, item: ConferenceProfile) => renderActions(item)
    }
  ];

  // Use data grid hook
  const {
    data,
    loading,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    columns,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useConferenceProfileList({ initialColumns: columnsWithActions });


  const handleCreate = useCallback(() => {
    navigate('/settings/pbx/conference-profiles/new');
  }, [navigate]);

  const handleEdit = useCallback((profile: ConferenceProfile) => {
    navigate(`/settings/pbx/conference-profiles/${profile.uuid ?? profile.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((profile: ConferenceProfile) => {
    navigateToConferenceProfileDuplicate(navigate, profile.uuid ?? profile.id);
  }, [navigate]);

  const handleDelete = useCallback(async (profile: ConferenceProfile) => {
    if (profile.isSystem) {
      showError(t('conferenceProfiles.messages.cannotDeleteSystem'));
      return;
    }

    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, [showError, t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      await deleteConferenceProfile(profileToDelete.id);

      showSuccess(t('conferenceProfiles.messages.deleteSuccess'));
      refresh();
      setShowDeleteModal(false);
      setProfileToDelete(null);
    } catch (error) {
      showError(error.message || t('conferenceProfiles.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, t, refresh, showSuccess, showError]);

  const handleViewXML = useCallback(async (profile: ConferenceProfile) => {
    try {
      const xmlData = await generateConferenceProfileXml(profile.id);
      setXmlModal({
        isOpen: true,
        profile: profile,
        xml: xmlData
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('conferenceProfiles.messages.xmlError');
      showError(errorMessage);
    }
  }, [t, showError]);

  // Action buttons
  const renderActions = useCallback((profile: ConferenceProfile) => {
    if (!profile) return null;

    return (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={profile.isSystem ? t('conferenceProfiles.messages.editSystemProfile') : t('conferenceProfiles.actions.edit')} position="top">
          <button
            onClick={() => handleEdit(profile)}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
          >
            <Edit className="h-4 w-4" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.actions.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(profile)}
            className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-md transition-colors"
          >
            <Copy className="h-4 w-4" />
          </button>
        </SimpleTooltip>

        {isDebugModeActive && (
          <SimpleTooltip content={t('common.actions.viewXML')} position="top">
            <button
              onClick={() => handleViewXML(profile)}
              className="p-2 text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-md transition-colors"
            >
              <Code className="h-4 w-4" />
            </button>
          </SimpleTooltip>
        )}

        {!profile.isSystem && (
          <SimpleTooltip content={t('common.actions.delete')} position="top">
            <button
              onClick={() => handleDelete(profile)}
              className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </SimpleTooltip>
        )}
      </div>
    );
  }, [handleEdit, handleDuplicate, handleViewXML, handleDelete, isDebugModeActive, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            title={t('conferenceProfiles.title')}
            titleIcon={<Settings className="h-5 w-5 text-primary" />}
            loading={loading}
            columns={columns}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            renderActions={renderActions}
            searchPlaceholder={t('conferenceProfiles.search.placeholder')}
            onAdd={handleCreate}
            addButtonText={t('common.add')}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            title={t('conferenceProfiles.messages.deleteConfirmTitle')}
            message={profileToDelete
              ? t('conferenceProfiles.messages.deleteConfirmWithName', { name: profileToDelete.name })
              : ''}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            isLoading={isDeleting}
            type="danger"
          />

          {/* XML View Modal */}
          {xmlModal.isOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('conferenceProfiles.xmlViewer.title')} - {xmlModal.profile?.name}
                  </h3>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, profile: null, xml: '' })}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {xmlModal.xml}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(xmlModal.xml);
                      showSuccess(t('conferenceProfiles.messages.xmlCopied'));
                    }}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    {t('conferenceProfiles.actions.copyXml')}
                  </button>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, profile: null, xml: '' })}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      }
      formView={null}
    />
  );
}
